import {
	Component,
	EventEmitter,
	Input,
	Output
}                                   from '@angular/core';
import { BaseComponent }            from '@app/core/bases/base-component/base.component';
import { LIST_OPERATIONS_METADATA } from '@shared/list-operations/config/list-operations.config';
import { ListOperationsEnum }       from '@shared/list-operations/model/list-operations.model';
import {
	IPaging,
	IPagingData,
	PagingModeEnum
}                                   from '@shared/list-operations/model/paging.model';
import { Subject }                  from 'rxjs';
import {
	debounceTime,
	distinctUntilChanged,
	takeUntil
}                                   from 'rxjs/operators';

@Component( {
	selector: 'app-pagination',
	templateUrl: './pagination.component.html',
	styleUrls: [ './pagination.component.scss' ]
} )
export class PaginationComponent extends BaseComponent {

	private _hasContinuesModeNextPageItems: boolean;

	private debounceTimer = 400;

	private prevPageNumber: number;

	private pagingInputSubject: Subject<number> = new Subject();

	maxPagesAsNumbers = 5;

	pageNumber: number;

	pageCount: number;

	pageCountList: number[];

	pageSize: number;

	pagingModeEnum = PagingModeEnum;

	pagingData: IPagingData;

	@Input( 'defaultPagingData' )
	set defaultPagingData( defaultPagingData: IPagingData ) {
		if ( defaultPagingData ) {
			this.pagingData = defaultPagingData;
			this.initialize();
			this.setPaging( this.pagingData.defaultValues );
			this.operationInitializedEmitter.emit( this.preparePagingForEmit );
		}
	}

	@Input( 'activeListOperation' )
	set activeListOperation( listOperation: ListOperationsEnum ) {
		this.showPagination = listOperation === ListOperationsEnum.PAGINATION;
	}

	@Input( 'pagingInput' )
	set pagingInput( paging: IPaging ) {
		if ( this.pagingData && paging ) {
			this.setPaging( paging );
		}
	}

	@Input( 'activeListOperationChange' )
	set activeListOperationChange( activeListOperation: ListOperationsEnum ) {
		setTimeout( () => {
			if ( this.pagingData?.mode === PagingModeEnum.Light || this.pagingData?.continuesPagesMode ) {
				this.showBody = true;
			} else if ( activeListOperation !== ListOperationsEnum.PAGINATION ) {
				this.showBody = false;
			}
		} );
	}

	@Output() operationChangedEmitter: EventEmitter<IPaging> = new EventEmitter();

	@Output() operationInitializedEmitter: EventEmitter<IPaging> = new EventEmitter();

	@Output() activeListOperationEmitter: EventEmitter<ListOperationsEnum> = new EventEmitter();

	showPagination: boolean;

	disableBackwardStep: boolean;

	disableForwardStep: boolean;

	activeOperation: boolean;

	showBody: boolean;


	constructor() {
		super();
	}

	private initialize(): void {
		this.disableBackwardStep = false;
		this.disableForwardStep = false;

		// currently component supports continuous mode only in light mode
		if ( this.pagingData?.continuesPagesMode && this.pagingData?.mode === PagingModeEnum.Full ) {
			this.logger.warn(
				'Pagination component does not support continuesPagesMode in FullMode, switched to LightMode.',
				'Please check provided pagingData',
				this.pagingData
			);
			this.pagingData.mode = PagingModeEnum.Light;
		}

		this.pagingInputSubject
		.pipe(
			debounceTime( this.debounceTimer ),
			distinctUntilChanged(),
			takeUntil( this.ngUnsubscribe )
		)
		.subscribe( ( pageNumber: number ) => {
			this.onUserInput( pageNumber );
		} );
	}

	private updateControlState(): void {
		this.disableBackwardStep = this.pageNumber === 1;
		this.disableForwardStep = this.pageNumber === this.pageCount;
	}

	private get preparePagingForEmit(): IPaging {
		const paging: IPaging = {
			pageNumber: this.pageNumber - 1,
			pageSize: this.pageSize
		};


		if ( this.prevPageNumber !== this.pageNumber ) {
			this.prevPageNumber = this.pageNumber;
		}

		return paging;
	}

	private hasContinuesModeNextPageItems( paging: IPaging ): boolean {
		const hasNoNextPageItems = paging.pageNumber === 0 && this.pageNumber > 1 ||
															 paging.uiListResultIsSmallerAsPageSize;

		return !hasNoNextPageItems;
	}

	private setPaging( paging: IPaging ): void {

		this.pageSize = paging.pageSize;

		// LIGHT MODE ONLY: creates array of paging numbers based on pageCount
		if (
			this.pagingData?.mode === PagingModeEnum.Light &&
			paging.pageCount <= this.maxPagesAsNumbers
		) {
			this.pageCountList = Array.from(
				{ length: paging.pageCount },
				( val, idx ) => idx + 1
			);
		}

		// ALL MODES
		if ( !this.pagingData?.continuesPagesMode ) {	// no continuesPagesMode condition
			this.pageNumber = paging.pageNumber + 1;
			this.prevPageNumber = this.pageNumber;
			this.pageCount = paging.pageCount ?? 1;
			this.updateControlState();
		} else { // continuesPagesMode condition
			// set page to 1 on resetPaging flag (flag is set by other ListOps changes)
			if ( paging.resetPaging ) {
				this.pageNumber = 1;
				this.prevPageNumber = this.pageNumber;
				return;
			}

			this._hasContinuesModeNextPageItems = this.hasContinuesModeNextPageItems( paging );

			// if no nextPage items exist
			if ( !this._hasContinuesModeNextPageItems ) {
				// stay on current page if pageSize is smaller
				if ( paging?.uiListResultIsSmallerAsPageSize ) {
					this.updateControlStateContinuesMode( paging );
				} else { // go to previous page if more pages than 1 exists, else go to first page
					this.pageNumber = this.pageNumber > 1 ? --this.pageNumber : 1;
					this.prevPageNumber = this.pageNumber;
					this.disableForwardStep = true;
					this.emitPagingChanges();
				}
			} else { // if nextPage items exist
				this.pageNumber = paging.pageNumber + 1;
				this.updateControlStateContinuesMode( paging );
			}
		}
	}

	private updateControlStateContinuesMode( paging: IPaging ): void {
		this.disableBackwardStep = this.pageNumber === 1;

		// disables next page button when no result
		if ( !this._hasContinuesModeNextPageItems ) {
			this.disableForwardStep = this.pageNumber > 1; // don't disable on first page
		}

		// and enables again when switched back to previous page
		if (
			paging.pageNumber === 0 && this.pageNumber === 1 &&
			!paging.uiListResultIsSmallerAsPageSize
		) {
			this.disableForwardStep = false;
		}
	}

	private emitPagingChanges(): void {
		this.operationChangedEmitter.emit( this.preparePagingForEmit );
	}

	resetListOperator() {
		this.pageNumber = 1;
		this.pageCount = 1;
		this.prevPageNumber = 1;
		this.pageCountList = [];
		this.pageSize = this.pagingData?.defaultValues.pageSize
										?? LIST_OPERATIONS_METADATA?.default?.paging?.pageSize ?? 20;

		this.activeOperation = false;
		if ( this.pagingData?.mode === PagingModeEnum.Light || this.pagingData?.continuesPagesMode ) {
			this.showBody = true;
		}
	}

	toggleBodyVisibility(): void {
		this.showBody = !this.showBody;
		if ( this.showBody ) { this.activeListOperationEmitter.emit( ListOperationsEnum.PAGINATION ); }
	}

	firstPage(): void {
		this.pageNumber = 1;
		this.updateControlState();
		this.emitPagingChanges();
	}

	prevPage(): void {
		this.pageNumber--;
		if ( this.pageNumber < 1 ) {
			this.firstPage();
			return;
		}

		this.updateControlState();
		this.emitPagingChanges();
	}

	nextPage(): void {
		this.pageNumber++;
		if ( this.pageNumber > this.pageCount ) {
			this.lastPage();
			return;
		}
		this.updateControlState();
		this.emitPagingChanges();
	}

	lastPage(): void {
		this.pageNumber = this.pageCount;
		this.updateControlState();
		this.emitPagingChanges();
	}

	onUserInput( pageNumber: number ): void {
		if ( !pageNumber || pageNumber === 0 ) { pageNumber = 1; }
		if ( pageNumber > this.pageCount ) { pageNumber = this.pageCount; }
		this.pageNumber = pageNumber;

		if ( this.prevPageNumber !== this.pageNumber ) {
			this.updateControlState();
			this.emitPagingChanges();
		}
	}

	onInputChange( evt: any ): void {
		const pageNumber = parseInt( evt.target.value, 10 );
		this.pagingInputSubject.next( pageNumber );
	}
}

